/**
 * Revoking a session the passkey flow minted but must not keep.
 *
 * Split out of `use-passkey-sign-in.ts` rather than inlined there because it is
 * the one part of that state machine with no React in it: every exit that has to
 * undo a sign-in routes through here, and the two SDK facts below are the whole
 * reason the calls are not one-liners at their call sites.
 *
 *  1. **Scope.** A bare `signOut()` revokes by USER — every tab, every device.
 *     Nothing this flow minted is worth signing somebody out of their phone for,
 *     so every teardown here is `scope: 'local'`, which revokes the CURRENT
 *     session and leaves the user's other sessions alone.
 *
 *     `local` still makes a server round trip, whatever the name suggests:
 *     `_signOut` posts the access token to `/logout` for EVERY scope, and only
 *     the blast radius differs. That is what makes point 2 matter — a call that
 *     reaches the network is a call that can fail, and a failed local revoke
 *     leaves the session sitting in storage.
 *  2. **Both channels.** `signOut()` reports a failed revoke on the RESOLVED
 *     channel (`Promise<{ error }>`) and rejects only when the call throws, so a
 *     `try`/`catch` alone reads a server-side failure as success. The difference
 *     is not cosmetic: on that path the SDK returns before its own local session
 *     removal, so the session survives in storage as well as on the server.
 */

import { withRequestDeadline } from '@/components/auth/passkey-deadlines';
import { clearAllAppData } from '@/lib/mobile/auth-persistence';
import { supabase } from '@/integrations/supabase/client';

/** Context-tagged error log. Optional at every call site, as in the hook. */
type LogError = (label: string, error: unknown) => void;

/**
 * The only two fields of a failed revoke that may reach a logger: an `AuthError`
 * keeps the originating failure on `cause`, and Sentry's linkedErrors
 * integration walks cause chains into telemetry.
 */
interface RevokeFailure {
  readonly name: string;
  readonly code?: string;
}

/**
 * Which session a ceremony minted, as the value a revoke is aimed at.
 *
 * Identity is asked as a question about the SESSION, not about which sign-in
 * method produced it — which is why it survives the user signing in by
 * password, by OAuth, in another tab, or on another device while this flow was
 * still in flight.
 *
 * Both fields are carried because neither answers alone. `session_id` is the
 * real discriminator: it is stable while the access token rotates under
 * auto-refresh, and a token-only comparison would call a refreshed session a
 * stranger and leave it alive. The token is the fallback for a JWT this code
 * cannot decode.
 *
 * `accessToken` MUST NOT be logged, transmitted, or put in an error context. It
 * is a bearer credential (`AGENTS.md` → "Security Boundaries" → "Secrets
 * handling"). `sessionId` is an identifier rather than a credential, but there
 * is no reason to log it either.
 */
export interface MintedSession {
  /**
   * The `session_id` claim, which is STABLE across a refresh while the access
   * token around it rotates. Null when the token could not be decoded, which is
   * why `accessToken` is still carried.
   */
  readonly sessionId: string | null;
  readonly accessToken: string;
}

/**
 * The `session_id` claim, decoded and never verified.
 *
 * That is the right amount of trust here and must stay that way: this value is
 * only ever compared against another value read from the same store, to decide
 * whether a teardown is aimed at the session this flow created. Nothing is
 * authorised on it. A forged token would only be able to make this flow decline
 * to revoke, which is the safe direction.
 */
function readSessionId(accessToken: string): string | null {
  try {
    const segment = accessToken.split('.')[1];
    if (segment === undefined) {
      return null;
    }
    // base64url -> base64, then pad: `atob` rejects an unpadded string.
    const base64 = segment.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { session_id?: unknown };
    return typeof claims.session_id === 'string' && claims.session_id !== ''
      ? claims.session_id
      : null;
  } catch {
    return null;
  }
}

/**
 * Whether two reads describe the same session.
 *
 * Prefers `session_id` because the access token rotates under auto-refresh: a
 * refreshed session compares unequal by token and would escape teardown,
 * leaving live the very aal1 session this flow exists to clean up. Token
 * equality remains the fallback for a token that cannot be decoded — it is
 * stricter than necessary, and being stricter means declining to revoke rather
 * than revoking something that may not be ours.
 */
function isSameSession(a: MintedSession, b: MintedSession): boolean {
  if (a.sessionId !== null && b.sessionId !== null) {
    return a.sessionId === b.sessionId;
  }
  return a.accessToken === b.accessToken;
}

/**
 * Reads the session currently in storage. Call it immediately after a ceremony
 * resolves: the SDK persists the session and emits `SIGNED_IN` BEFORE the
 * sign-in call returns, so by then this reads the session that ceremony minted.
 *
 * `null` means the SDK has no session to hand back — nothing was minted, it has
 * already been replaced and removed, or the read could not answer inside its
 * window. Every one of those reads as "there is nothing this flow may claim",
 * so every caller treats null as "do not tear anything down".
 */
export async function captureMintedSession(): Promise<MintedSession | null> {
  try {
    return await withRequestDeadline(readStoredSession(), null);
  } catch {
    // An unreadable store is indistinguishable from an empty one here, and the
    // conservative reading of both is "claim nothing".
    return null;
  }
}

async function readStoredSession(): Promise<MintedSession | null> {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  return accessToken === undefined ? null : { accessToken, sessionId: readSessionId(accessToken) };
}

/**
 * Whether the session in storage RIGHT NOW is still the one this flow minted.
 *
 * Exported because teardown is not the only decision that needs it: admitting a
 * user is the other one. `use-passkey-sign-in.ts` guards its continuations on a
 * mount epoch, which answers "does this continuation belong to the mount that
 * started it" — a different question, and one a retry running in the SAME mount
 * passes even after a password or OAuth sign-in has replaced the session
 * underneath it.
 *
 * A null `minted` answers false: this flow never established what it was
 * holding, so it can prove nothing about what is there now. Callers decide
 * whether that is a reason to act; the ones that TEAR DOWN must not, and the one
 * that SIGNS IN treats it as the pre-existing "never knew" case.
 */
export async function stillHoldsMintedSession(minted: MintedSession | null): Promise<boolean> {
  if (minted === null) {
    return false;
  }
  const live = await captureMintedSession();
  return live !== null && isSameSession(live, minted);
}

export interface PasskeySessionTeardown {
  /**
   * Revokes the session this flow minted, and only if it is still the session in
   * storage.
   *
   * The guard is the whole point. Every caller runs in a continuation that can
   * outlive the attempt that started it — a capped ceremony settling minutes
   * later, an unmount compensation, a dismissed step-up — by which time the user
   * may have signed in again by ANY method. Revoking on "no newer passkey
   * attempt started" cannot see a password or OAuth sign-in at all; revoking on
   * "the stored session is still mine" sees all of them.
   *
   * A failed revoke does NOT leave the session in storage. `scope: 'local'` is no
   * storage-only escape hatch - it makes the same round trip and returns before
   * its own removal on exactly this path - so the chain ends in
   * `clearAllAppData()`, and ownership is re-proven immediately before that
   * clear rather than inherited from the guard above (the revoke between them is
   * bounded at thirty seconds).
   *
   * Residual that remains: the SERVER-side session survives until it expires
   * whenever the revoke could not reach the server, and nothing here emits
   * SIGNED_OUT, so this tab's in-memory React state outlives the purge until a
   * reload. The route guard's step-up check is the remaining challenger for
   * both. Every failure is logged, never swallowed.
   */
  readonly revokeMintedSession: (label: string, minted: MintedSession | null) => Promise<void>;
}

export function createPasskeySessionTeardown(logError?: LogError): PasskeySessionTeardown {
  const revokeMintedSession = async (
    label: string,
    minted: MintedSession | null,
  ): Promise<void> => {
    if (minted === null) {
      return;
    }
    const live = await captureMintedSession();
    if (live === null || !isSameSession(live, minted)) {
      // Something else owns the storage slot now. Its session is not this
      // flow's to revoke, and not this flow's to clear out from under it
      // either. The orphaned session survives server-side until it expires.
      return;
    }

    let failed: RevokeFailure | null = null;
    try {
      failed = await withRequestDeadline<RevokeFailure | null>(revokeCurrentSession(), {
        name: 'PasskeyRevokeTimeout',
      });
    } catch (error) {
      failed = { name: error instanceof Error ? error.name : 'unknown' };
    }
    if (failed === null) {
      return;
    }
    logError?.(label, failed);

    // The revoke removed nothing, so the aal1 session is still in storage
    // waiting for the next visitor. `scope: 'local'` is no escape hatch from
    // that: it makes the same round trip and fails the same way, which is why
    // the chain has to end somewhere that touches storage directly.
    //
    // `clearAllAppData()` is the documented single owner of auth-storage
    // teardown (`lib/mobile/auth-persistence.ts`) - the store of record differs
    // per shell (localStorage on web, the bridge's authStorage on desktop, the
    // Keychain on native), so clearing one key by hand silently no-ops on the
    // others.
    //
    // Ownership is re-proven HERE, not inherited from the guard at the top. That
    // guard ran before an await bounded at `AUTH_REQUEST_TIMEOUT_MS`, so up to
    // thirty seconds can pass before this line - ample room for a password or
    // OAuth sign-in to land and replace the stored session. This call is
    // unconditional destruction of the whole auth store rather than a revoke
    // aimed at one token, so it is the last place in this file that may act on
    // a stale answer.
    //
    // Narrowed, not closed: this is still check-then-act against a store the SDK
    // owns, so a sign-in landing between this read and the clear would still be
    // caught by it. The residual is microseconds instead of thirty seconds, and
    // there is no client-side primitive that does better - `clearAllAppData()`
    // takes no session to match against.
    const stillOurs = await captureMintedSession();
    if (stillOurs === null || !isSameSession(stillOurs, minted)) {
      return;
    }

    try {
      await clearAllAppData();
    } catch (error) {
      logError?.(`${label}Purge`, {
        name: error instanceof Error ? error.name : 'unknown',
      });
    }
  };

  return { revokeMintedSession };
}

async function revokeCurrentSession(): Promise<RevokeFailure | null> {
  const { error } = await supabase.auth.signOut({ scope: 'local' });
  return error === null ? null : { name: error.name, code: error.code };
}
